import { useCallback, useEffect, useRef, useState } from 'react'
import { ThemeChips } from '../components/ThemeChips'
import { useMainButton } from '../components/MainButton'
import { TarotCard } from '../components/TarotCard'
import { CardZoom, type ZoomedCard } from '../components/CardZoom'
import { RichText } from '../components/RichText'
import { cardById, cardMeaning, cardName, loc } from '../data/cards'
import {
  positionLabels,
  repeatCost,
  repeatableToday,
  spreadById,
  spreadName,
  yesNoAnswer,
  yesNoLabel,
  FOLLOWUP_COST,
  FOLLOWUP_TURNS,
  type SpreadId,
} from '../data/spreads'
import { formatDate, type T } from '../i18n'
import { revealField } from '../lib/viewport'
import { api, ApiError } from '../lib/api'
import { shareCard } from '../lib/share'
import { adsAvailable, showRewardAd } from '../lib/ads'
import { sameDraw, type AskLine, type DrawnCard } from '../lib/storage'
import { haptic } from '../lib/telegram'
import { useSirr } from '../store'
import { track } from '../lib/analytics'

interface Props {
  spread: SpreadId
  cards: DrawnCard[]
  /** Точка отсчёта прогноза: месяц для Колеса года, день недели для Недели. */
  startMonth: number
  /** Что человек сравнивает в «Двух путях»; уходит в промпт как вопрос. */
  question?: string
  /** Сервер отказал по лимиту или по нехватке звёзд — уводим на Premium. */
  onPaywall: (reason: 'limit' | 'stars') => void
  /**
   * Готовое толкование уже сделанного сегодня расклада. Если оно есть, карты
   * показываются раскрытыми, толкование берётся отсюда, а к модели не ходим и
   * повторно не списываем — это тот же расклад, а не новый.
   */
  saved?: {
    /** id с сервера — по нему задаются вопросы. */
    id?: string
    overall: string
    perCard: string[]
    /** Отложенная выдача: пока не наступил, текста нет и показывать нечего. */
    readyAt?: number
    chat?: AskLine[]
    turnsLeft?: number
    /** Тема, с которой расклад сделан: по ней экран решает, менялась ли она. */
    theme?: string
  } | null
  /** Осознанно вытянуть заново (новый расклад со списанием). Для «Карты дня» — нет. */
  onNewReading?: () => void
  /** Сделать этот же расклад под выбранной сейчас темой. */
  onRetheme?: () => void
  /**
   * Сервер отдал уже сделанный сегодня расклад с другими картами: правило
   * «один расклад в день» держит он, а история на устройстве могла не успеть
   * подтянуться. Показать надо его карты, иначе доска разойдётся с текстом.
   */
  onRepeated?: (cards: DrawnCard[], saved: { overall: string; perCard: string[] }) => void
}

/**
 * Блок вопроса по готовому раскладу.
 *
 * **Объявлен снаружи `Reading`, и это не вкусовщина.** Пока он был вложенной
 * функцией, React на каждом рендере видел новый тип компонента: поддерево
 * размонтировалось, `<textarea>` создавался заново, фокус слетал — и
 * клавиатура закрывалась после КАЖДОЙ буквы. Набрать вопрос удавалось только
 * по одному символу, всякий раз заново нажимая на поле. Вложенный компонент
 * рядом с полем ввода — всегда эта ошибка, даже когда состояние живёт
 * у родителя.
 */
function AskBlock({
  t,
  chat,
  question2,
  setQuestion2,
  asking,
  askError,
  turnsLeft,
  onAsk,
}: {
  t: T
  chat: AskLine[]
  question2: string
  setQuestion2: (v: string) => void
  asking: boolean
  askError: boolean
  turnsLeft: number
  onAsk: () => void
}) {
  const started = chat.length > 0
  return (
    <div style={{ marginTop: 18, borderTop: '1px solid var(--line)', paddingTop: 14 }}>
      <div className="eyebrow" style={{ marginBottom: started ? 12 : 4 }}>
        {t('askTitle')}
      </div>

      {/* Цену называем один раз, до первого вопроса: дальше они уже
          оплачены, и повтор цены выглядел бы как плата за каждый. */}
      {!started && (
        <p style={{ fontSize: 12, color: 'var(--ink-muted)', margin: '0 0 12px', lineHeight: 1.4 }}>
          {t('askIntro', { t: FOLLOWUP_TURNS, n: FOLLOWUP_COST })}
        </p>
      )}

      <div className="talk__list">
        {chat.map((line, i) => (
          <div key={i} className={`bubble bubble--${line.who}`}>
            {line.who === 'p' ? <RichText text={line.text} /> : line.text}
          </div>
        ))}
        {asking && (
          <div className="bubble bubble--p bubble--typing">
            <span />
            <span />
            <span />
          </div>
        )}
      </div>

      {askError && (
        <p style={{ fontSize: 12, color: '#e07a54', margin: '10px 0 0', lineHeight: 1.4 }}>
          {t('askFailed')}
        </p>
      )}

      {turnsLeft > 0 ? (
        <>
          <textarea
            className="askfield"
            rows={2}
            value={question2}
            onChange={(e) => setQuestion2(e.target.value)}
            // Поле лежит в самом низу длинной страницы: клавиатура ужимает
            // каркас, и оно уходит под неё. Показываем его сами — прокруткой
            // одной ленты, `scrollIntoView` здесь запрещён (он двигает и каркас).
            onFocus={(e) => revealField(e.currentTarget)}
            placeholder={t('askHint')}
            maxLength={300}
            style={{ marginTop: 12, resize: 'none' }}
          />
          <button
            type="button"
            className="pick"
            disabled={asking || question2.trim().length === 0}
            style={{
              marginTop: 10,
              padding: 12,
              textAlign: 'center',
              fontSize: 13,
              color: 'var(--gold)',
              opacity: asking || question2.trim().length === 0 ? 0.5 : 1,
            }}
            onClick={() => {
              haptic.impact('medium')
              onAsk()
            }}
          >
            {started ? t('askSend') : `${t('askSend')} · ${FOLLOWUP_COST} ✦`}
          </button>
          {started && (
            <p style={{ fontSize: 11, color: 'var(--ink-faint)', margin: '8px 0 0', textAlign: 'center' }}>
              {t('askLeft', { n: turnsLeft })}
            </p>
          )}
        </>
      ) : (
        <p style={{ fontSize: 11.5, color: 'var(--ink-faint)', margin: '12px 0 0', textAlign: 'center', lineHeight: 1.5 }}>
          {t('askOver')}
        </p>
      )}
    </div>
  )
}

type Interp =
  | { status: 'idle' }
  | { status: 'loading' }
  /** Толкование готово, но показывать его ещё рано (отложенная выдача). */
  | { status: 'waiting'; readyAt: number }
  | { status: 'ok'; overall: string; perCard: string[]; source: 'ai' | 'local' }

export function Reading({
  spread, cards, startMonth, question, onPaywall, saved, onNewReading, onRetheme, onRepeated,
}: Props) {
  const { profile, lang, t, addReading, setRemaining, toggleFavorite, update } = useSirr()

  const [revealed, setRevealed] = useState(saved ? cards.length : 0)
  const [interp, setInterp] = useState<Interp>(
    saved
      ? (saved.readyAt ?? 0) > Date.now()
        // Расклад открыт из истории, но он ещё «готовится»: текста в записи нет,
        // и показывать его как готовый значило бы показать пустоту.
        ? { status: 'waiting', readyAt: saved.readyAt! }
        : { status: 'ok', overall: saved.overall, perCard: saved.perCard, source: 'ai' }
      : { status: 'idle' },
  )
  /**
   * id расклада на сервере: приходит с толкованием, а при переоткрытии — из
   * истории. Без него вопросы задавать некуда.
   */
  /**
   * Тема, с которой сделан ЭТОТ расклад. Не `profile.theme`: та меняется прямо
   * на этом экране, и сравнивать надо с той, что была в момент запроса.
   */
  const [madeWith, setMadeWith] = useState(saved?.theme ?? '')

  /** Сделан ли сегодня этот расклад под ВЫБРАННОЙ сейчас темой. */
  const madeForTheme = profile.history.some(
    (r) =>
      r.spread === spread &&
      r.ts >= new Date().setHours(0, 0, 0, 0) &&
      (r.theme ?? '') === (profile.theme ?? '') &&
      Boolean(r.overall),
  )
  const [readingId, setReadingId] = useState<string | null>(saved?.id ?? null)
  const [chat, setChat] = useState<AskLine[]>(saved?.chat ?? [])
  const [turnsLeft, setTurnsLeft] = useState(saved?.turnsLeft ?? FOLLOWUP_TURNS)
  /** Во сколько раз ужать доску, чтобы она влезла в узкий экран. */
  const [boardScale, setBoardScale] = useState(1)
  const boardRef = useRef<HTMLDivElement>(null)
  /** Масштаб ряда карт: у раскладов «в ряд» своя мерка, см. эффект ниже. */
  const [rowScale, setRowScale] = useState(1)
  const rowRef = useRef<HTMLDivElement>(null)

  const def = spreadById(spread)
  const labels = positionLabels(spread, lang, startMonth)
  const allRevealed = revealed >= cards.length

  // Расклад сохраняется в историю ровно один раз, когда толкование готово.
  // Открытый из истории (saved) уже там — повторно не сохраняем.
  const savedRef = useRef(Boolean(saved))

  // Колбэк из App пересоздаётся на каждый его рендер. Держим его в ref, иначе
  // он попадёт в зависимости эффекта и запрос будет перезапускаться без конца.
  const paywallRef = useRef(onPaywall)
  paywallRef.current = onPaywall
  const repeatedRef = useRef(onRepeated)
  repeatedRef.current = onRepeated

  // Цели и знак — тоже в ref, и по той же причине, но цена ошибки здесь выше.
  // Ответ синхронизации приносит эти поля с сервера заново, и `goals`
  // оказывается новым массивом с тем же содержимым. Как зависимость эффекта
  // это перезапускало запрос толкования по кругу, списывая звёзды за каждый
  // повтор: Колесо года по 200 ✦ до полного обнуления баланса.
  const promptRef = useRef({ goals: profile.goals, zodiac: profile.zodiac, theme: profile.theme })
  promptRef.current = { goals: profile.goals, zodiac: profile.zodiac, theme: profile.theme }

  /** Локальное толкование из справочника — запасной вариант, если AI недоступен. */
  const localInterpretation = useCallback(() => {
    const perCard = cards.map((c) => cardMeaning(cardById(c.id), lang, c.reversed))
    const names = cards.map((c) => cardName(cardById(c.id), lang)).join(' · ')
    const overall = loc(
      {
        ru: `Карты сложились в единый узор: ${names}. Вглядитесь в связь между ними — в ней и лежит ответ на ваш вопрос.`,
        az: `Kartlar vahid naxış yaratdı: ${names}. Aralarındakı bağa baxın — cavab məhz orada gizlənir.`,
        en: `The cards form a single pattern: ${names}. Look at what connects them — the answer to your question lies there.`,
        tr: `Kartlar tek bir desen oluşturdu: ${names}. Aralarındaki bağa bakın — sorunuzun cevabı orada.`,
      },
      lang,
    )
    return { overall, perCard }
  }, [cards, lang])

  const fetchInterpretation = useCallback(
    async (signal?: AbortSignal) => {
      setInterp({ status: 'loading' })
      try {
        const { theme, ...prompt } = promptRef.current
        const res = await api.interpret(
          { spread, cards, lang, startMonth, question, ...prompt, theme: theme ?? undefined },
          signal,
        )
        setRemaining(res.remaining)
        if (typeof res.stars === 'number') update({ stars: res.stars })

        // Сервер отдал сохранённый расклад, и карты в нём другие — показываем
        // его карты. Иначе на доске одно, а в толковании другое.
        if (res.repeated && res.cards && !sameDraw(res.cards, cards)) {
          repeatedRef.current?.(res.cards, { overall: res.overall, perCard: res.perCard })
          return
        }

        setReadingId(res.id)
        // Отложенная выдача: текста в ответе нет вовсе — сервер его придержал.
        if (res.readyAt && res.readyAt > Date.now()) {
          setInterp({ status: 'waiting', readyAt: res.readyAt })
          return
        }
        setInterp({ status: 'ok', overall: res.overall, perCard: res.perCard, source: 'ai' })
        track('reading_done', { spread, source: 'ai' })
        haptic.notify('success')
      } catch (err) {
        if (signal?.aborted) return
        if (err instanceof ApiError && err.code === 'limit_reached') {
          paywallRef.current('limit')
          return
        }
        if (err instanceof ApiError && err.code === 'insufficient_stars') {
          paywallRef.current('stars')
          return
        }
        // Бэкенд недоступен (или мы вне Telegram) — показываем справочные
        // значения карт, чтобы расклад всё равно был полезен.
        const local = localInterpretation()
        setInterp({ status: 'ok', ...local, source: 'local' })
        track('reading_done', { spread, source: 'local' })
      }
    },
    [spread, cards, lang, startMonth, question, setRemaining, update, localInterpretation],
  )

  /**
   * За один расклад платим один раз.
   *
   * Даже со стабильным колбэком выше эффект может перезапуститься — от правки
   * пропсов, от перемонтирования. Толкование одного и того же расклада
   * заказывается ровно однажды: ключ считается по картам, а не по времени.
   */
  const requestedRef = useRef<string | null>(null)

  // Зависеть от interp.status здесь нельзя: запрос сам переводит статус в
  // loading, эффект перезапустился бы и отменил собственный же запрос.
  useEffect(() => {
    // Открытый из истории расклад толкование уже несёт — к модели не идём.
    if (saved) return
    if (!allRevealed) return
    const key = `${spread}:${cards.map((c) => `${c.id}${c.reversed ? 'r' : ''}`).join(',')}:${lang}`
    if (requestedRef.current === key) return
    requestedRef.current = key
    setMadeWith(promptRef.current.theme ?? '')
    track('reading_open', { spread, cost: def.cost })
    const ctrl = new AbortController()
    void fetchInterpretation(ctrl.signal)
    return () => ctrl.abort()
  }, [allRevealed, fetchInterpretation, spread, cards, lang, saved])

  useEffect(() => {
    // Сохраняем и ожидающий расклад: иначе он есть только на сервере, и до
    // первой синхронизации человек его в истории не найдёт. Текста в нём нет —
    // сервер его придержал, — и синхронизация пустым текстом ничего не затрёт.
    // Когда срок выйдет, текст приедет ближайшей синхронизацией: при слиянии
    // историй выигрывает серверная запись.
    if ((interp.status !== 'ok' && interp.status !== 'waiting') || savedRef.current) return
    savedRef.current = true
    addReading({
      // id с сервера, а не свой: по нему задаются вопросы по раскладу, и
      // придуманный локально сервер бы не нашёл.
      id: readingId ?? `${Date.now()}-${spread}`,
      ts: Date.now(),
      spread,
      cards,
      lang,
      overall: interp.status === 'ok' ? interp.overall : '',
      perCard: interp.status === 'ok' ? interp.perCard : [],
      readyAt: interp.status === 'waiting' ? interp.readyAt : 0,
      // Тема, под которую расклад и заказан. Берём из ref, где она замерла на
      // момент запроса: человек мог сменить её на главном экране, пока читал,
      // а запись должна помнить ту, с которой расклад делался, — иначе правило
      // «один в день на тему» будет считать не то.
      theme: promptRef.current.theme ?? '',
    })
  }, [interp, addReading, spread, cards, lang])

  /**
   * Ожидание готового расклада.
   *
   * Текст лежит на сервере и придержан там же — на клиенте его нет, прятать
   * нечего. Поэтому дожидаемся срока и спрашиваем заново. Опрашиваем раз в
   * пять секунд, а не считаем по часам устройства: они могут врать, а решает
   * всё равно сервер.
   */
  useEffect(() => {
    if (interp.status !== 'waiting' || !readingId) return
    const id = setInterval(() => {
      void api
        .reading(readingId)
        .then((res) => {
          if (res.readyAt > Date.now()) return
          setInterp({ status: 'ok', overall: res.overall, perCard: res.perCard, source: 'ai' })
          track('reading_done', { spread, source: 'ai' })
          haptic.notify('success')
        })
        .catch(() => {
          /* сеть подвела — спросим на следующем круге */
        })
    }, 5000)
    return () => clearInterval(id)
  }, [interp.status, readingId, spread])

  /** Сколько минут осталось ждать, округляя вверх: «0 мин» выглядит поломкой. */
  const minutesLeft =
    interp.status === 'waiting' ? Math.max(1, Math.ceil((interp.readyAt - Date.now()) / 60_000)) : 0

  const [revealing, setRevealing] = useState(false)
  /** Ролик не показался или награда не дошла — сказать об этом словами. */
  const [adFailed, setAdFailed] = useState(false)

  /**
   * Открыть досрочно.
   *
   * Сначала просто просим сервер: подписчику он откроет сразу, и рекламу
   * показывать незачем. Отказ означает «нужен просмотр» — тогда крутим ролик и
   * просим ещё раз.
   *
   * Досмотр подтверждает сервер AdsGram обратным вызовом, а не этот код, —
   * поэтому после ролика мы не «начисляем», а спрашиваем заново. Вызов идёт
   * server-to-server и может отстать на секунду, отсюда несколько попыток.
   */
  const revealNow = async () => {
    if (!readingId || revealing) return
    setRevealing(true)
    setAdFailed(false)
    try {
      const opened = await tryReveal()
      if (opened) return

      if (!adsAvailable()) {
        onPaywall('stars')
        return
      }
      const watched = await showRewardAd()
      if (!watched) {
        // Ролик не показался или закрыт на середине. Молчать нельзя: человек
        // нажал «Открыть сейчас», а экран не изменился.
        setAdFailed(true)
        return
      }

      for (let attempt = 0; attempt < 4; attempt++) {
        await new Promise((r) => setTimeout(r, 700))
        if (await tryReveal()) return
      }
      // Ролик досмотрен, а подтверждение так и не дошло. Молчать нельзя:
      // человек честно потратил время.
      setAskError(true)
      setAdFailed(true)
      haptic.notify('error')
    } finally {
      setRevealing(false)
    }
  }

  /** Одна попытка открыть. `false` — сервер не пустил (нужен просмотр). */
  const tryReveal = async (): Promise<boolean> => {
    try {
      const res = await api.revealReading(readingId!)
      setInterp({ status: 'ok', overall: res.overall, perCard: res.perCard, source: 'ai' })
      haptic.notify('success')
      return true
    } catch (err) {
      if (err instanceof ApiError && err.code === 'reveal_denied') return false
      haptic.notify('error')
      return false
    }
  }

  const revealNext = () => {
    haptic.impact('light')
    setRevealed((n) => Math.min(cards.length, n + 1))
  }

  const [question2, setQuestion2] = useState('')
  const [asking, setAsking] = useState(false)
  const [askError, setAskError] = useState(false)

  /**
   * Вопрос по готовому раскладу.
   *
   * Списание, счёт вопросов и возврат при сбое — на сервере; здесь только
   * отправка и показ. Балансом и остатком верим ответу, а не своей арифметике:
   * человек мог задать вопрос с другого устройства.
   */
  const ask = async () => {
    const text = question2.trim()
    if (!text || asking || !readingId) return
    setAsking(true)
    setAskError(false)
    // Свою реплику показываем сразу — ждать ответа с пустым экраном незачем.
    setChat((c) => [...c, { who: 'u', text }])
    setQuestion2('')
    try {
      const res = await api.askReading(readingId, text, startMonth)
      setChat(res.chat)
      setTurnsLeft(res.turnsLeft)
      if (typeof res.balance === 'number') update({ stars: res.balance })
      haptic.notify('success')
    } catch (err) {
      // Свою реплику убираем: вопрос не задан, и висеть ей не на чем.
      setChat((c) => c.slice(0, -1))
      setQuestion2(text)
      if (err instanceof ApiError && err.code === 'not_enough_stars') {
        onPaywall('stars')
      } else {
        setAskError(true)
        haptic.notify('error')
      }
    } finally {
      setAsking(false)
    }
  }


  const [sharing, setSharing] = useState(false)

  /**
   * Какая карта раскрыта во весь экран.
   *
   * В Кельтском кресте и Колесе года рисунок размером с ноготь: разглядеть
   * его нельзя, а разглядывать карту людям хочется. Толкование берём то же,
   * что показано в списке ниже, — от модели, а при её отказе из справочника.
   */
  const [zoomed, setZoomed] = useState<ZoomedCard | null>(null)

  const openCard = (index: number) => {
    const c = cards[index]
    const card = cardById(c.id)
    haptic.impact('light')
    setZoomed({
      id: card.id,
      sym: card.sym,
      rank: card.rank,
      name: cardName(card, lang),
      label: labels[index] ?? '',
      meaning:
        interp.status === 'ok'
          ? interp.perCard[index] || cardMeaning(card, lang, c.reversed)
          : cardMeaning(card, lang, c.reversed),
      reversed: c.reversed,
    })
  }

  /**
   * Подпись к отправленной карточке.
   *
   * Одного названия расклада мало: получатель видит картинку и не понимает,
   * что это и откуда. Даём название, выпавшие карты и начало толкования —
   * ссылку-приглашение допишет сервер, имя бота знает только он.
   */
  const shareCaption = () => {
    const names = cards.map((c) => cardName(cardById(c.id), lang)).join(' · ')
    const excerpt =
      interp.status === 'ok'
        ? interp.overall.length > 300
          ? `${interp.overall.slice(0, 300).replace(/\s+\S*$/, '')}…`
          : interp.overall
        : ''
    return [`✦ ${spreadName(def, lang)}`, names, excerpt].filter(Boolean).join('\n\n')
  }

  const share = async () => {
    if (interp.status !== 'ok' || sharing) return
    haptic.impact('medium')
    setSharing(true)
    try {
      const outcome = await shareCard(
        {
          title: spreadName(def, lang),
          tagline: t('tagline'),
          date: formatDate(Date.now(), lang),
          text: interp.overall,
          cards: cards.map((c, i) => {
            const card = cardById(c.id)
            return {
              id: card.id,
              sym: card.sym,
              rank: card.rank,
              name: cardName(card, lang),
              label: labels[i] ?? '',
              reversed: c.reversed,
            }
          }),
        },
        shareCaption(),
        lang,
      )
      track('share_done', { outcome, spread })
      haptic.notify(outcome === 'failed' ? 'error' : 'success')
    } catch {
      haptic.notify('error')
    } finally {
      setSharing(false)
    }
  }

  useMainButton(
    allRevealed
      ? interp.status === 'ok'
        ? { label: t('share'), loading: sharing, onClick: () => void share() }
        // При ожидании главное действие — открыть сейчас, а не «Карты говорят…»:
        // та подпись обещает работу, которой не идёт, и ни на что не нажимается.
        : interp.status === 'waiting'
          ? { label: t('waitNow'), onClick: () => {
              haptic.impact('medium')
              void revealNow()
            } }
          : { label: t('reading'), onClick: () => {}, loading: interp.status === 'loading' }
      : { label: t('revealAll'), onClick: () => {
          haptic.impact('medium')
          setRevealed(cards.length)
        } },
  )

  const layout = def.layout

  /*
    Доска занимает всю доступную ширину — и когда она шире экрана, и когда уже.

    Раньше здесь стояло «ужимаем, только если не влезает». Ужималось верно, а
    вот УЖЕ экрана оказались почти все доски: у «Чувств» она 210 точек при
    доступных ~316, то есть сто точек ширины пропадали впустую, и карта выходила
    вдвое мельче, чем в раскладе из трёх. Замерено и починено сперва в нативе,
    здесь то же самое.

    Потолок по высоте — 54% окна: при большем расклад выпихивал толкование за
    первый экран, и текст приходилось искать листанием (проверено на живом
    Android). Считаем от окна, а не числом: на планшете и на 320 точках это
    разное.
  */
  useEffect(() => {
    const el = boardRef.current
    if (layout.kind !== 'absolute' || !el || !layout.boardW) return
    const fit = () => {
      const byWidth = el.clientWidth / layout.boardW!
      const byHeight = layout.boardH ? (window.innerHeight * 0.54) / layout.boardH : 1.9
      setBoardScale(Math.min(1.9, byWidth, byHeight))
    }
    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(el)
    window.addEventListener('resize', fit)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', fit)
    }
  }, [layout])

  /*
    Ряд карт тоже растягивается под ширину.

    Масштабировались только доски с координатами, а ряды рисовались натуральной
    величиной — и у «Карты дня» с «Да или нет» больше половины ширины пустовало:
    одна карта в 130 точек при доступных ~316. Та же беда, что была у
    «Подковы», просто в другом месте.

    Считаем по РЯДУ, а не по всем картам: они переносятся по ширине.

    Потолок ниже, чем у досок (1.6 против 1.9): одна карта во весь экран
    читается уже не как расклад, а как обложка.
  */
  useEffect(() => {
    const el = rowRef.current
    if (layout.kind !== 'linear' || !el) return
    const gap = 12
    const fit = () => {
      const available = el.clientWidth
      if (!available) return
      const perRow = Math.max(1, Math.min(cards.length, Math.floor((available + gap) / (layout.cardW + gap))))
      const rowW = perRow * layout.cardW + (perRow - 1) * gap
      const byHeight = (window.innerHeight * 0.54) / layout.cardH
      // Одиночную карту не растягиваем: по расчёту выходило 208×330, почти во
      // весь экран, и на устройстве это оказалось слишком — расклад из одной
      // карты так читается как обложка. Правило то же, что в нативе.
      setRowScale(cards.length > 1 ? Math.min(1.6, available / rowW, byHeight) : 1)
    }
    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(el)
    window.addEventListener('resize', fit)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', fit)
    }
  }, [layout, cards.length])

  return (
    <div className="screen" style={{ paddingTop: 18 }}>
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 24 }}>
          {spreadName(def, lang)}
        </div>
        {!allRevealed && (
          <div style={{ fontFamily: 'var(--serif-body)', fontSize: 15, color: 'var(--ink-muted)', marginTop: 2 }}>
            {t('tapToReveal')}
          </div>
        )}
      </div>

      {/*
        Тема — до открытия карт, и это единственный момент, когда её выбор
        что-то значит: толкование заказывается ровно тогда, когда открыта
        последняя карта. До этого к модели не ушло ничего.

        После раскрытия полосу убираем: текст уже написан под ту тему, что
        была, и переключатель обещал бы то, чего не сделает.
      */}
      {!allRevealed && !saved && <ThemeChips compact />}

      {layout.kind === 'linear' ? (
        <div ref={rowRef} style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
          {cards.map((c, i) => {
            const card = cardById(c.id)
            const faceUp = i < revealed
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                  animation: 'fu .5s both',
                  animationDelay: `${i * 0.08}s`,
                }}
              >
                <TarotCard
                  faceUp={faceUp}
                  cardId={card.id}
                  sym={card.sym}
                  rank={card.rank}
                  title={cardName(card, lang)}
                  reversed={c.reversed}
                  width={Math.round(layout.cardW * rowScale)}
                  height={Math.round(layout.cardH * rowScale)}
                  showTitle={layout.showTitles}
                  openLabel={t('openCard')}
                  onClick={faceUp ? () => openCard(i) : revealNext}
                />
                {/* В «Да или нет» подпись позиции дублирует плашку с ответом
                    прямо под ней — там она лишняя. */}
                {spread !== 'yesno' && (
                  <div
                    className="eyebrow"
                    style={{ fontSize: 8.5, letterSpacing: 0.5, color: 'rgba(200,162,78,.8)' }}
                  >
                    {labels[i] ?? ''}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        /*
          Три уровня, и каждый нужен.

          Внешний меряет доступную ширину (её нельзя брать у того, чью ширину мы
          сами и задаём — вышла бы петля). Средний равен доске ПОСЛЕ
          масштабирования и центрирует её. Внутренний — доска в натуральную
          величину, отмасштабированная от левого верхнего угла.

          Средний появился не сразу: пока доска только ужималась, центрировать
          было нечего — она всегда занимала всю ширину. С потолком по высоте
          масштаб может упереться в неё, и тогда доска УЖЕ доступной ширины;
          без этого уровня она прижималась к левому краю. Поймано владельцем на
          «Пути»: крест стоял левее середины экрана.
        */
        <div
          ref={boardRef}
          style={{ width: '100%', height: (layout.boardH ?? 0) * boardScale, marginBottom: 8 }}
        >
          <div style={{ width: (layout.boardW ?? 0) * boardScale, margin: '0 auto' }}>
          <div
            style={{
              position: 'relative',
              width: layout.boardW,
              height: layout.boardH,
              // Ужатая доска масштабируется от левого верхнего угла и ровно
              // заполняет доступную ширину; в натуральную величину — центрируется.
              // Доска масштабируется от левого верхнего угла и ровно заполняет
              // доступную ширину — в обе стороны, и при ужатии, и при растяжении.
              margin: 0,
              transform: `scale(${boardScale})`,
              transformOrigin: 'top left',
            }}
          >
            {cards.map((c, i) => {
            const card = cardById(c.id)
            const faceUp = i < revealed
            const slot = layout.slots?.[i] ?? { x: 0, y: 0 }
            return (
              // Поворот держим на отдельном вложенном элементе: анимация
              // появления заканчивается на `transform: none` и с fill-mode
              // both затёрла бы инлайновый rotate на том же узле.
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: slot.x,
                  top: slot.y,
                  width: layout.cardW,
                  height: layout.cardH,
                  zIndex: slot.z ?? 1,
                  animation: 'fu .5s both',
                  animationDelay: `${i * 0.08}s`,
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    transform: slot.rot ? `rotate(${slot.rot}deg)` : undefined,
                  }}
                >
                  <TarotCard
                    faceUp={faceUp}
                    cardId={card.id}
                    sym={card.sym}
                    rank={card.rank}
                    title={cardName(card, lang)}
                    /*
                      Подписи позиций на картах НЕ рисуем — ни на каких.

                      Порог «влезает или нет» переписывался трижды и дважды
                      перемерялся на устройстве: кегль подписи почти не
                      уменьшается вместе с картой, поэтому на мелких досках
                      слова ломались посреди себя («Сове т», «Наст оящ…»), а на
                      крупных подпись налезала на номер карты. И всё это ради
                      текста, который и так есть: под доской идёт список позиций
                      с полной подписью и толкованием каждой.

                      В озвучку кнопки подпись по-прежнему уходит (`openLabel`).
                    */
                    label=""
                    reversed={c.reversed}
                    showTitle={layout.showTitles}
                    openLabel={`${labels[i] ?? ''} — ${t('openCard')}`}
                    onClick={faceUp ? () => openCard(i) : revealNext}
                  />
                  </div>
                </div>
              )
            })}
          </div>
          </div>
        </div>
      )}

      {allRevealed && spread === 'yesno' && cards[0] && (
        <div className="verdict">
          <div className="verdict__label">{t('answerLabel')}</div>
          <div className="verdict__value">{yesNoLabel(yesNoAnswer(cards[0]), lang)}</div>
        </div>
      )}

      {allRevealed && (
        <div style={{ animation: 'fu .8s both', marginTop: 10 }}>
          {/* Отложенная выдача: текста нет и на клиенте — сервер его придержал.
              Показываем срок и единственный честный выход, а не пустой блок. */}
          {interp.status === 'waiting' && (
            <div
              style={{
                border: '1px solid var(--line-strong)',
                borderRadius: 16,
                padding: '22px 20px',
                marginBottom: 12,
                textAlign: 'center',
                background: 'linear-gradient(180deg, rgba(74,44,20,.4), rgba(18,10,31,.3))',
              }}
            >
              <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 19 }}>
                {t('waitTitle')}
              </div>
              <div style={{ fontSize: 13.5, lineHeight: 1.45, color: 'var(--ink-muted)', marginTop: 6 }}>
                {t('waitReady', { m: minutesLeft })}
              </div>
              {/*
                Кнопка занимает ВСЮ ширину плашки, а не обнимает текст: узкая
                пилюля с текстом впритык к рамке выглядела скукоженной, особенно
                на азербайджанском, где «İndi aç» — два слова.

                Оформление второстепенное, контуром: внизу экрана стоит главная
                кнопка с тем же действием, и две одинаково яркие спорили бы.
              */}
              <button
                type="button"
                style={{
                  display: 'block',
                  width: '100%',
                  marginTop: 14,
                  padding: '13px 16px',
                  background: 'transparent',
                  border: '1px solid var(--gold)',
                  borderRadius: 14,
                  color: 'var(--gold-bright)',
                  fontFamily: 'var(--sans)',
                  fontSize: 15,
                  fontWeight: 600,
                }}
                onClick={() => {
                  haptic.impact('medium')
                  void revealNow()
                }}
              >
                {t('waitNow')}
              </button>
              <div style={{ fontSize: 12, lineHeight: 1.4, color: 'var(--ink-faint)', marginTop: 10 }}>
                {adFailed ? t('adFailed') : adsAvailable() ? t('waitByAd') : t('waitBySub')}
              </div>
            </div>
          )}

          <div
            style={{
              border: '1px solid var(--line)',
              borderRadius: 16,
              padding: 18,
              background: 'linear-gradient(180deg, rgba(45,26,74,.45), rgba(18,10,31,.3))',
            }}
          >
            <div className="eyebrow" style={{ marginBottom: 10 }}>
              {interp.status === 'loading' ? t('reading') : t('interpretation')}
            </div>

            {/* Имена карт показываем сразу, как только расклад раскрыт: на мелких
                картах названий нет, и ждать ответа модели, чтобы узнать, что
                выпало, — неправильно. Ожидание видно только на самих текстах. */}
            {interp.status === 'loading' || interp.status === 'waiting' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 16 }}>
                {[100, 92, 78].map((w) => (
                  <div key={w} className="shimmer" style={{ width: `${w}%` }} />
                ))}
              </div>
            ) : (
              <p className="prose" style={{ fontSize: 18, color: '#e2d6ef', marginBottom: 16 }}>
                {interp.status === 'ok' ? interp.overall : ''}
              </p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              {cards.map((c, i) => {
                    const card = cardById(c.id)
                    const fav = profile.favorites.includes(c.id)
                    return (
                      <div key={i} style={{ display: 'flex', gap: 11 }}>
                        <div
                          style={{
                            width: 30,
                            height: 30,
                            flexShrink: 0,
                            borderRadius: 8,
                            border: '1px solid rgba(200,162,78,.35)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 15,
                            color: 'var(--gold)',
                            transform: c.reversed ? 'rotate(180deg)' : undefined,
                          }}
                        >
                          {card.sym}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 15 }}>
                            {labels[i] && (
                              <span
                                className="eyebrow"
                                style={{ fontSize: 9, letterSpacing: 1, marginRight: 6, color: 'rgba(200,162,78,.8)' }}
                              >
                                {labels[i]}
                              </span>
                            )}
                            {cardName(card, lang)}
                            {c.reversed && (
                              <span style={{ fontSize: 11, color: 'var(--ink-faint)' }}> · {t('reversed')}</span>
                            )}
                          </div>
                          {interp.status === 'ok' ? (
                            <div
                              style={{
                                fontFamily: 'var(--serif-body)',
                                fontSize: 15,
                                lineHeight: 1.4,
                                color: '#c9bcd8',
                                marginTop: 2,
                              }}
                            >
                              {interp.perCard[i] || cardMeaning(card, lang, c.reversed)}
                            </div>
                          ) : (
                            <div className="shimmer" style={{ width: '85%', marginTop: 6 }} />
                          )}
                        </div>
                        <button
                          type="button"
                          aria-pressed={fav}
                          aria-label={t('favorite')}
                          onClick={() => {
                            haptic.select()
                            toggleFavorite(c.id)
                          }}
                          style={{ color: fav ? 'var(--gold)' : 'var(--ink-faint)', fontSize: 16, padding: '0 2px' }}
                        >
                          {fav ? '★' : '☆'}
                        </button>
                      </div>
                    )
                  })}
            </div>

            {interp.status === 'ok' && interp.source === 'local' && (
              <div style={{ marginTop: 14, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
                <p style={{ fontSize: 12, color: 'var(--ink-faint)', margin: 0, lineHeight: 1.4 }}>
                  {t('aiFailed')}
                </p>
                <button
                  type="button"
                  className="pick"
                  style={{ marginTop: 10, padding: 11, textAlign: 'center', fontSize: 13 }}
                  onClick={() => {
                    haptic.impact('light')
                    void fetchInterpretation()
                  }}
                >
                  {t('retry')}
                </button>
              </div>
            )}
          </div>

          {interp.status === 'ok' && (
            <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--ink-faint)', marginTop: 12 }}>
              {t('saved')}
            </p>
          )}

          {/* Вытянуть заново можно только те расклады, чей ответ зависит от
              заданного вопроса: его человек задаёт заново. Остальные отвечают
              на положение дел, а оно за вечер не меняется — второй такой же
              расклад противоречил бы первому и списал бы ещё раз. */}
          {interp.status === 'ok' && repeatableToday(spread) && onNewReading && (
            <button
              type="button"
              className="pick"
              style={{ marginTop: 14, padding: 12, textAlign: 'center', fontSize: 13, color: 'var(--gold)' }}
              onClick={() => {
                haptic.impact('medium')
                onNewReading()
              }}
            >
              {def.cost > 0 ? `${t('newReading')} · ${def.cost} ✦` : t('newReading')}
            </button>
          )}

          {/*
            Повтор под другой темой — здесь же, а не «пойдите на главную».

            Подпись зовёт выбрать другую тему, и без этой полосы она была бы
            обещанием без исполнения: чипы на экране расклада прячутся после
            раскрытия, а на главной их ещё надо найти и вернуться в каталог.

            Кнопка появляется, только когда выбранная тема ОТЛИЧАЕТСЯ от той, с
            которой сделан этот расклад: тапнуть свою же тему и получить новые
            карты значило бы перекидывать их до нужного ответа.
          */}
          {interp.status === 'ok' && !repeatableToday(spread) && (
            <>
              <p style={{ textAlign: 'center', fontSize: 11.5, color: 'var(--ink-faint)', marginTop: 14, lineHeight: 1.5 }}>
                {t('onceADay')}
              </p>
              <ThemeChips compact />
              {(profile.theme ?? '') !== madeWith && onRetheme && (
                <button
                  type="button"
                  className="pick"
                  style={{ marginTop: 10, padding: 12, textAlign: 'center', fontSize: 13, color: 'var(--gold)' }}
                  onClick={() => {
                    haptic.impact('medium')
                    // Без «заново любой ценой»: если под этой темой расклад
                    // сегодня уже был, человек получит его же и не заплатит.
                    onRetheme()
                  }}
                >
                  {/* Под выбранной темой расклад мог быть сегодня уже сделан —
                      тогда кнопка откроет его, а не создаст новый. */}
                  {madeForTheme
                    ? t('showMade')
                    : def.cost > 0
                      ? `${t('newReading')} · ${repeatCost(def.cost)} ✦`
                      : t('newReading')}
                </button>
              )}
            </>
          )}

          {/* Вопросы по раскладу. Показываем только когда толкование от модели:
              на запасном тексте из справочника спрашивать не о чем — его писала
              не модель, и разговор о нём вести некому. */}
          {interp.status === 'ok' && interp.source === 'ai' && readingId && <AskBlock
              t={t}
              chat={chat}
              question2={question2}
              setQuestion2={setQuestion2}
              asking={asking}
              askError={askError}
              turnsLeft={turnsLeft}
              onAsk={() => void ask()}
            />}
        </div>
      )}

      <CardZoom
        card={zoomed}
        reversedLabel={t('reversedCard')}
        closeLabel={t('closeCard')}
        onClose={() => setZoomed(null)}
      />
    </div>
  )
}
